import {
  afterNextRender,
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import {CurrentObjectContext} from '../current-object.context';
import {OverviewFieldsModule} from '../../overview-fields/overview-fields.module';
import {ObjectViewModule} from '../../object-view/object-view.module';
import {OperationsModule} from '../../operations/operations.module';
import {MatCard} from '@angular/material/card';
import {ObjectOverviewFieldsV2Component} from '../ui/object-overview-fields-v2/object-overview-fields-v2.component';
import {TranslateModule} from '@ngx-translate/core';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CdkPortal} from '@angular/cdk/portal';
import {ObjectPageLayoutComponent} from '../object-page-layout/object-page-layout.component';
import {NgTemplateOutlet, NgClass} from '@angular/common';
import {Section} from '../../core/definitions/sections-container';
import {MatListItem, MatNavList} from '@angular/material/list';
import {MediaSelectorModule} from "../../media-selector/media-selector.module";
import {isOutsideView, SaveScrollRestorer} from './save-scroll-restorer';

@Component({
    selector: 'app-object-page-v2-overview',
    imports: [CdkPortal, NgTemplateOutlet, OverviewFieldsModule, ObjectViewModule, OperationsModule, MatCard, ObjectOverviewFieldsV2Component, TranslateModule, MatProgressSpinner, MatNavList, MatListItem, NgClass, MediaSelectorModule],
    templateUrl: './object-page-v2-overview.component.html',
    styleUrl: './object-page-v2-overview.component.scss'
})
export class ObjectPageV2OverviewComponent implements AfterViewInit, DoCheck, OnDestroy {
  // Injections
  private readonly currentObjectContext: CurrentObjectContext = inject(CurrentObjectContext);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly injector: Injector = inject(Injector);

  readonly layout = inject(ObjectPageLayoutComponent);
  @ViewChild(CdkPortal, {static: true}) sectionNavigation: CdkPortal;

  constructor() {
    afterNextRender(() => this.layout.sectionNavigation.set(this.sectionNavigation));
  }

  private _hiddenSectionNavigator = signal(true);
  private readonly saveScrollRestorer = new SaveScrollRestorer();

  get hiddenSectionNavigator() {
    return this._hiddenSectionNavigator.asReadonly();
  }

  @ViewChild('scrollOffsetAnchor') scrollOffsetAnchor: ElementRef;

  ngAfterViewInit() {
    const scrollContainer = this.elementRef.nativeElement.closest('mat-sidenav-content');
    const observer = new IntersectionObserver(([entry]) => {
      this._hiddenSectionNavigator.set(entry.isIntersecting);
    }, {
      root: scrollContainer,
      rootMargin: '250px 0px 0px 0px',
    });

    observer.observe(this.scrollOffsetAnchor.nativeElement);

    this.cleanup = () => observer.disconnect();
  }

  ngDoCheck() {
    const sectionId = this.saveScrollRestorer.checkReload(this.sectionsContainer, this.isEditing);
    if (sectionId) {
      afterNextRender(() => this.scrollBackToSection(sectionId), {injector: this.injector});
    }
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    this.saveScrollRestorer.rememberFocus(event.target);
  }

  private cleanup = () => {};
  ngOnDestroy() {
    this.cleanup();
    if (this.layout.sectionNavigation() === this.sectionNavigation) {
      this.layout.sectionNavigation.set(null);
    }
  }

  get sectionsContainer() {
    return this.currentObjectContext.sectionsContainer
  }

  get isEditing() {
    return this.currentObjectContext.isEditing
  }

  get isNew() {
    return this.currentObjectContext.isNew;
  }

  get contentInfo() {
    return this.currentObjectContext.contentInfo
  }

  get operationContainer() {
    return this.currentObjectContext.operationContainer;
  }

  get operationStep() {
    return this.currentObjectContext.operationContainer?.currentOperation?.$$currentStep;
  }

  get loading() {
    return this.currentObjectContext.loadingArtifact;
  }

  scrollToSection(section: Section) {
    const el = document.getElementById(`section-${section.name}`);
    const scrollContainer = el?.closest('mat-sidenav-content');

    if (el && scrollContainer) {
      this.scrollTo(el, scrollContainer);
    }
  }

  private scrollBackToSection(sectionId: string) {
    const el = document.getElementById(sectionId);
    const scrollContainer = el?.closest('mat-sidenav-content');

    if (el && scrollContainer && isOutsideView(el, scrollContainer)) {
      this.scrollTo(el, scrollContainer);
    }
  }

  private scrollTo(el: HTMLElement, scrollContainer: Element) {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    scrollContainer.scrollTo({ top: el.offsetTop - (this.layout.isMobile() ? 0 : 50), behavior });
  }
}
